import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Lock, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ea_admin_token";
const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "bg-black/50 border-white/20 text-white placeholder:text-neutral-600 rounded-none h-12 focus-visible:ring-gold focus-visible:ring-offset-0 focus-visible:border-gold/60";

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const PaymentBadge = ({ status }) => {
  const map = {
    paid: { label: "Paid", cls: "bg-gold text-black" },
    pending: { label: "Pending", cls: "border border-white/25 text-neutral-400" },
    failed: { label: "Failed", cls: "border border-red-900 text-red-400" },
    expired: { label: "Expired", cls: "border border-white/15 text-neutral-600" },
    none: { label: "No checkout", cls: "border border-white/15 text-neutral-600" },
  };
  const { label, cls } = map[status] || map.none;
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 ${cls}`} data-testid={`payment-badge-${status}`}>
      {label}
    </span>
  );
};

const LoginForm = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="w-full max-w-md bg-ink-surface border border-white/10 p-10"
      data-testid="admin-login-card"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 border border-gold/60 rotate-45 flex items-center justify-center">
          <Lock className="w-4 h-4 text-gold -rotate-45" />
        </span>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Owner Access Only</p>
          <h1 className="font-serif text-2xl text-white tracking-tight">Elevated Apex Dashboard</h1>
        </div>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Email</Label>
          <Input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@elevatedapexco.com"
            className={inputCls}
            data-testid="admin-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Password</Label>
          <Input
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="••••••••••••"
            className={inputCls}
            data-testid="admin-password-input"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter Dashboard"}
        </button>
      </form>
    </motion.div>
  );
};

const ClientRow = ({ client, token, onToggle }) => {
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/intakes/${client.id}/documents`,
        { documents_received: !client.documents_received },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onToggle(client.id, !client.documents_received);
      toast.success(`${client.name}: documents ${!client.documents_received ? "marked received" : "unmarked"}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      className="grid lg:grid-cols-12 gap-4 items-center bg-ink-surface border border-white/10 px-6 py-5 hover:border-gold/30 transition-colors duration-300"
      data-testid={`client-row-${client.id}`}
    >
      <div className="lg:col-span-4">
        <p className="font-serif text-lg text-white">{client.name}</p>
        <p className="text-xs text-neutral-500 font-light mt-0.5">{client.email} — {client.phone}</p>
      </div>
      <div className="lg:col-span-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">Submitted</p>
        <p className="text-sm text-neutral-300 mt-1">
          {client.created_at ? new Date(client.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
        </p>
      </div>
      <div className="lg:col-span-2 flex items-center gap-3">
        <PaymentBadge status={client.payment_status} />
        {client.payment_status === "paid" && client.amount != null && (
          <span className="font-mono text-xs text-gold">${(client.amount / 100).toFixed(0)}</span>
        )}
      </div>
      <div className="lg:col-span-4 flex lg:justify-end">
        <button
          onClick={toggle}
          disabled={saving}
          className={`flex items-center gap-3 border px-4 py-2.5 transition-colors duration-300 disabled:opacity-60 ${
            client.documents_received
              ? "border-gold/60 bg-gold/10 text-gold"
              : "border-white/20 text-neutral-400 hover:border-gold/50 hover:text-gold"
          }`}
          data-testid={`docs-checkbox-${client.id}`}
        >
          <span className={`w-4 h-4 border flex items-center justify-center ${client.documents_received ? "border-gold bg-gold" : "border-current"}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : client.documents_received && <Check className="w-3 h-3 text-black" />}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em]">
            Documents {client.documents_received ? "received" : "pending"}
          </span>
        </button>
      </div>
    </motion.div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadClients = async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/intakes`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setClients(data.clients);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setAuthed(true);
        loadClients(token);
      })
      .catch(() => logout())
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAuthed(false);
    setClients([]);
    setChecking(false);
  };

  const paid = clients.filter((c) => c.payment_status === "paid").length;
  const docs = clients.filter((c) => c.documents_received).length;

  if (checking) {
    return (
      <main className="bg-ink-base min-h-screen flex items-center justify-center" data-testid="admin-checking">
        <Loader2 className="w-6 h-6 animate-spin text-gold" />
      </main>
    );
  }

  if (!authed) {
    return (
      <main className="bg-ink-base min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <LoginForm onLogin={(tok) => { setToken(tok); setAuthed(true); loadClients(tok); }} />
      </main>
    );
  }

  return (
    <main className="bg-ink-base min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-2xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 border border-gold/60 rotate-45 flex items-center justify-center">
              <span className="w-2 h-2 bg-gold -rotate-45" />
            </span>
            <div>
              <p className="font-serif text-lg text-white">Elevated <span className="text-gold italic">Apex</span> — Owner Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadClients(token)}
              className="flex items-center gap-2 border border-white/20 text-neutral-300 text-[11px] font-mono uppercase tracking-[0.15em] px-4 py-2.5 hover:border-gold hover:text-gold transition-colors duration-300"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 border border-white/20 text-neutral-300 text-[11px] font-mono uppercase tracking-[0.15em] px-4 py-2.5 hover:border-red-800 hover:text-red-400 transition-colors duration-300"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-3 gap-4" data-testid="admin-stats">
          {[
            ["Total clients", clients.length],
            ["Paid", paid],
            ["Documents received", docs],
          ].map(([label, value]) => (
            <div key={label} className="bg-ink-surface border border-white/10 px-6 py-5">
              <p className="font-serif text-3xl text-gold">{value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 space-y-3">
          {loading && clients.length === 0 ? (
            <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gold" /></div>
          ) : clients.length === 0 ? (
            <div className="border border-white/10 bg-ink-surface px-6 py-16 text-center" data-testid="admin-empty-state">
              <p className="font-serif text-xl text-neutral-400">No client intakes yet.</p>
              <p className="mt-2 text-sm text-neutral-600 font-light">New intake submissions will appear here automatically.</p>
            </div>
          ) : (
            clients.map((c) => (
              <ClientRow
                key={c.id}
                client={c}
                token={token}
                onToggle={(id, val) => setClients((prev) => prev.map((x) => (x.id === id ? { ...x, documents_received: val } : x)))}
              />
            ))
          )}
        </div>
      </div>
    </main>
  );
}
